import { createClient } from "@supabase/supabase-js";
import type { Database } from "./database.types";
import { supabase } from "./supabase";

// Environment variables
const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || "";
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || "";
const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY || "";

// Cache for client instances
const clientCache = new Map<
  string,
  ReturnType<typeof createClient<Database>>
>();

/**
 * Creates a Supabase client for API routes with user token authentication.
 *
 * **Do not cache per bearer token:** JWT strings share an identical base64 header
 * prefix (e.g. `eyJhbGciOi...`), so `token.substring(0, 10)` would collide for
 * every access token and even after refresh — PostgREST would keep using a stale
 * `Authorization` header and return `PGRST301` / JWT expired.
 *
 * Only the shared anon client (no token) is cached.
 */
export function createApiSupabaseClient(token?: string) {
  const options = token
    ? {
        global: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      }
    : {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
      };

  if (token) {
    return createClient<Database>(supabaseUrl, supabaseAnonKey, options);
  }

  const cacheKey = "anon";
  if (clientCache.has(cacheKey)) {
    return clientCache.get(cacheKey)!;
  }

  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, options);
  clientCache.set(cacheKey, client);
  return client;
}

/**
 * Authenticate user with timeout protection
 * Wraps getUser() call with a timeout to prevent hanging requests
 */
export async function authenticateUserWithTimeout(
  apiClient: ReturnType<typeof createApiSupabaseClient>,
  timeoutMs: number = 5000,
): Promise<{ user: { id: string } | null; error: Error | null }> {
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error("Authentication request timed out"));
      }, timeoutMs);
    });

    const result = await Promise.race([
      apiClient.auth.getUser(),
      timeoutPromise,
    ]);

    return {
      user: result.data?.user ?? null,
      error: result.error ?? null,
    };
  } catch (error) {
    // Handle timeout errors
    if (error instanceof Error) {
      if (
        error.message.includes("timeout") ||
        error.message.includes("aborted")
      ) {
        return {
          user: null,
          error: new Error(
            "Authentication request timed out. Please check your network connection.",
          ),
        };
      }

      // Handle socket/network errors
      if (
        error.message.includes("fetch failed") ||
        error.message.includes("socket") ||
        error.message.includes("closed")
      ) {
        return {
          user: null,
          error: new Error(
            "Network error during authentication. Please check your connection and try again.",
          ),
        };
      }

      return {
        user: null,
        error: error,
      };
    }

    return {
      user: null,
      error: new Error("Unknown authentication error"),
    };
  }
}

/**
 * Creates a Supabase client with service role for admin operations
 * Using a cache to prevent multiple instances
 */
export function createServiceSupabaseClient() {
  const cacheKey = "service";

  if (clientCache.has(cacheKey)) {
    return clientCache.get(cacheKey)!;
  }

  if (!supabaseServiceKey) {
    console.error("Missing SUPABASE_SERVICE_ROLE_KEY environment variable");
    throw new Error("Server configuration error: Missing service role key");
  }

  const client = createClient<Database>(supabaseUrl, supabaseServiceKey);
  clientCache.set(cacheKey, client);

  return client;
}

/**
 * Creates a cookie-based Supabase client for API routes
 */
export function createCookieSupabaseClient(cookieHeader?: string) {
  const cacheKey = cookieHeader
    ? `cookie-${cookieHeader.substring(0, 20)}`
    : "no-cookie";

  if (clientCache.has(cacheKey)) {
    return clientCache.get(cacheKey)!;
  }

  const client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    global: cookieHeader
      ? {
          headers: {
            cookie: cookieHeader,
          },
        }
      : undefined,
  });

  clientCache.set(cacheKey, client);
  return client;
}

/**
 * Check if a user has a profile
 */
export async function checkUserProfile(userId: string) {
  console.log("Checking profile for user:", userId);

  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      // If the error is PGRST116 (not found), return null instead of throwing
      if (error.code === "PGRST116") {
        console.log("No profile found for user");
        return null;
      }
      console.error("Error checking user profile:", error);
      throw error;
    }

    console.log("Profile check result:", data);
    return data;
  } catch (error) {
    console.error("Error in checkUserProfile:", error);
    throw error;
  }
}

interface CreateProfileParams {
  id: string;
  name: string;
  email: string;
  phone: string;
  location?: string;
  location_state_id?: string | null;
  location_lga_id?: string | null;
  location_neighborhood_id?: string | null;
  coordinates?: { lat: number; lng: number };
  avatar_url?: string | null;
  is_admin?: boolean;
  preferences?: {
    pushNotifications: boolean;
    emailNotifications: boolean;
    marketingNotifications: boolean;
  };
  privacy_settings?: {
    profileVisibility: boolean;
    activityVisibility: boolean;
  };
  gender?: string;
  place_id?: string | null;
  is_google_user?: boolean;
  is_profile_complete?: boolean;
  discovery_channel?: string | null;
}

interface CreateProfileApiParams extends CreateProfileParams {
  referral_code?: string | null;
}

interface CreateProfileApiResponse {
  success: boolean;
  message?: string;
  data?: unknown;
  error?: string;
}

/**
 * Create a new user profile with default settings
 */
export async function createProfile(params: CreateProfileParams) {
  // Set default values for preferences and privacy settings
  // Note: Respect is_profile_complete from params if provided, otherwise default to false
  const defaultProfile = {
    ...params,
    is_admin: false,
    preferences: {
      pushNotifications: true,
      emailNotifications: true,
      marketingNotifications: false,
    },
    privacy_settings: {
      profileVisibility: true,
      activityVisibility: true,
    },
    // Only set to false if not explicitly provided in params
    is_profile_complete:
      params.is_profile_complete !== undefined
        ? params.is_profile_complete
        : false,
  };

  // Use upsert instead of insert to handle existing profiles
  const { data, error } = await supabase
    .from("profiles")
    .upsert([defaultProfile], {
      onConflict: "id",
      ignoreDuplicates: false,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating profile:", error);
    throw error;
  }

  return data;
}

export async function createProfileViaApi(params: CreateProfileApiParams) {
  const response = await fetch("/api/create-profile", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });

  const payload = (await response.json()) as CreateProfileApiResponse;
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || "Failed to create profile");
  }
  return payload.data;
}
